import type { ReactiveController, ReactiveControllerHost, TemplateResult } from 'lit'
import type { Question } from '../contract'
import { css, html, nothing } from 'lit'
import { iconCaretDown, iconCaretUp, iconChecks } from './icons'

// 自繪 dropdown（Figma State=Open 133:699）。
//
// 為什麼不用原生 <select>：展開後的選單由 OS 繪製，macOS / Windows / iOS / Android 各長各的，
// 設計稿要的「選中列 brand 底 + 雙勾」在原生選單裡做不出來。代價是失去 iOS 滾輪與 Android
// sheet，這是「對齊設計、體驗跨平台一致」換來的（PM 決策 2026-07-31）。
//
// 為什麼面板是 <dialog showModal> 而不是 absolute / fixed：
//   1. top layer 會蓋過宿主站任何 z-index。widget 嵌在第三方新聞站，sticky header、廣告、
//      cookie banner 都可能疊在我們上面；面板留在自己的 shadow root 裡就得跟它們搶堆疊。
//   2. position:fixed 會被任何帶 transform / filter / will-change 的祖先綁架（宿主站很常有），
//      top layer 不受影響。
//   3. Esc 關閉、focus trap、::backdrop、背景 inert 都是原生行為，不必自己實作。
// 註：dialog.show()（非 modal）**不會**進 top layer，只有 showModal() 會 —— 所以這裡是 modal
// 語意，backdrop 透明化、點 backdrop 關閉。SDK 的 sidebar/modal overlay 已用同一機制。
//
// 實測（2026-07-31，Chrome）：`container-type: inline-size` 不會為 abspos/fixed 建立
// containing block，且 .frame / .frame-body / .q-body 目前都不是 positioned，所以 absolute
// 面板「現在」其實不會被裁 —— 但那只要任何祖先哪天加上 position:relative 就會無聲失效。
// 選 top layer 是為了不依賴這個巧合。

// ---------------------------------------------------------------------------
// 鍵盤導覽（WAI-ARIA listbox）
// ---------------------------------------------------------------------------

// 回傳新的 active index；null = 這顆鍵不歸方向鍵模型管，呼叫端自行處理（Enter/Esc/型別字元）。
// 兩端 clamp 而不是 wrap：wrap 會讓使用者以為清單沒有盡頭。
export function nextActiveIndex(key: string, current: number, count: number): number | null {
  if (count <= 0)
    return null
  // 尚未有 active（-1）時，任一方向鍵都從第一個開始。
  switch (key) {
    case 'ArrowDown':
      return current < 0 ? 0 : Math.min(current + 1, count - 1)
    case 'ArrowUp':
      return current < 0 ? 0 : Math.max(current - 1, 0)
    case 'Home':
      return 0
    case 'End':
      return count - 1
    default:
      return null
  }
}

// type-ahead：找出第一個以 buffer 開頭的選項（不分大小寫），找不到回 null。
// 單字元 buffer 從 from 的**下一個**開始找，所以連按同一個鍵會在共用前綴的選項之間循環；
// 多字元 buffer 從 from **本身**開始找，所以持續打字是在精煉目前這筆而不是跳走。
export function typeAheadIndex(labels: string[], buffer: string, from: number): number | null {
  const count = labels.length
  if (count === 0 || buffer === '')
    return null
  const needle = buffer.toLowerCase()
  const start = buffer.length === 1 ? from + 1 : Math.max(from, 0)
  for (let i = 0; i < count; i++) {
    const idx = (((start + i) % count) + count) % count
    if ((labels[idx] ?? '').toLowerCase().startsWith(needle))
      return idx
  }
  return null
}

// Type-ahead 緩衝是 module-level 的：面板是 modal，全頁同時只可能有一個展開中的 dropdown，
// 所以不需要 per-instance 狀態。開啟時由 controller 清空。
const TYPE_AHEAD_RESET_MS = 700
let typeBuffer = ''
let typeBufferAt = 0

export function resetTypeAhead(): void {
  typeBuffer = ''
  typeBufferAt = 0
}

function pushTypeAhead(char: string): string {
  const now = Date.now()
  if (now - typeBufferAt > TYPE_AHEAD_RESET_MS)
    typeBuffer = ''
  typeBuffer += char
  typeBufferAt = now
  return typeBuffer
}

// ---------------------------------------------------------------------------
// 面板放置
// ---------------------------------------------------------------------------

export interface PanelPlacementInput {
  triggerTop: number
  triggerBottom: number
  panelHeight: number
  viewportHeight: number
  /** 面板與視窗上下緣之間留的空隙。欄位與面板之間**不留**空隙（Figma 133:699 面板緊貼欄位下緣）。 */
  margin?: number
}

export interface PanelPlacement {
  top: number
  maxHeight: number
  placement: 'below' | 'above'
}

// 優先放在欄位下方且緊貼下緣；放不下就翻到上方；兩側都放不下就選空間大的一側並把高度壓進可用空間
// （面板自己會捲動）。座標是 viewport 基準 —— 面板在 top layer，position:fixed 以視窗為準。
export function panelPlacement(opts: PanelPlacementInput): PanelPlacement {
  const margin = opts.margin ?? 8
  const below = opts.viewportHeight - opts.triggerBottom - margin
  const above = opts.triggerTop - margin

  if (opts.panelHeight <= below)
    return { top: opts.triggerBottom, maxHeight: below, placement: 'below' }
  if (opts.panelHeight <= above)
    return { top: opts.triggerTop - opts.panelHeight, maxHeight: above, placement: 'above' }
  if (below >= above)
    return { top: opts.triggerBottom, maxHeight: below, placement: 'below' }
  return { top: margin, maxHeight: above, placement: 'above' }
}

// ---------------------------------------------------------------------------
// 量測 seam
// ---------------------------------------------------------------------------

// 面板定位需要的所有幾何量。panelPlacement 只吃得上半段；left/width 是把面板對齊觸發框用的。
export interface PanelMetrics extends PanelPlacementInput {
  triggerLeft: number
  triggerWidth: number
}

// 定位唯一碰環境的地方。抽成 seam 是因為 jsdom 這一項真的做不到 —— getBoundingClientRect 與
// offsetHeight 一律回 0（已實測），所以沒有替身就沒有任何一條測試能驗定位。
// controller 的其餘環境接觸（寫 style、requestAnimationFrame、window 上的 scroll/resize
// listener、focus）在 jsdom 都能跑，因此**刻意不抽**：包成 adapter 只會多一層轉呼叫，
// 真正欠的是測試而不是抽象層。
export interface PanelMeasure {
  // 面板未受 max-height clamp 時的自然高度只在每次開啟量一次（解除 clamp 後讀 offsetHeight 是
  // write→read 的強制重排）。展開期間選項不變，所以捲動重新定位時沿用同一個值。
  measure: (dialog: HTMLElement, trigger: HTMLElement) => PanelMetrics
  // 下一次 measure 重新量自然高度。開啟／關閉／宿主卸載時呼叫。
  invalidate: () => void
}

export function domPanelMeasure(): PanelMeasure {
  let naturalHeight: number | undefined
  return {
    invalidate() {
      naturalHeight = undefined
    },
    measure(dialog, trigger) {
      if (naturalHeight === undefined) {
        dialog.style.maxHeight = ''
        naturalHeight = dialog.offsetHeight
      }
      const r = trigger.getBoundingClientRect()
      return {
        triggerTop: r.top,
        triggerBottom: r.bottom,
        triggerLeft: r.left,
        triggerWidth: r.width,
        panelHeight: naturalHeight,
        viewportHeight: window.innerHeight,
      }
    },
  }
}

// ---------------------------------------------------------------------------
// 樣式（Figma State=Open 133:699）
// ---------------------------------------------------------------------------

// 面板的量測只有一份：component library 的 State=Open 沒有分斷點的變體，而觸發框的字級
// （18px）在兩個斷點本來就相同，所以面板在 mobile / desktop 用同一組數字。觸發框自己的
// padding 仍照原本的斷點規則（mobile 313:3496 py8/44 列高；desktop 280:2734 pl16 pr12 py12）。
export const dropdownStyles = css`
  /* 觸發框：全框線 + radius/8、18px Regular、右側 20px Caret。改成 <button> 之後 caret 是
     真的 flex 子元素（不再是 background-image），所以右 padding 回到設計稿的 12 而不是為了
     讓出背景圖而留的 36。 */
  .dropdown-trigger {
    display: flex;
    width: 100%;
    box-sizing: border-box;
    min-height: 44px;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
    padding: 8px 12px;
    border: 1px solid var(--mp-border-default);
    border-radius: 8px;
    background: var(--mp-bg-base);
    color: var(--mp-text-primary);
    font: inherit;
    font-size: 18px;
    /* 行高寫死 26（= Figma Value 文字框高）而不是吃繼承的 ~27：mobile 8+26+8+2 剛好是設計稿的
       44 列高，交給繼承行高會多 1px。選項列用同一個 26。 */
    line-height: 26px;
    letter-spacing: 0.5px;
    text-align: left;
    cursor: pointer;
  }
  /* ⚠️ 這個 container query 必須留在 base 規則**之後** —— 兩者特異性同為 (0,1,0)，靠順序決勝。
     main 上的舊 .dropdown-select 正是把 base 寫在 container query 後面，導致桌機規格從未生效
     （2026-07-31 實測：frame 720 時 .line-input 已套用桌機值，.dropdown-select 仍停在手機值）。 */
  @container frame (min-width: 640px) {
    .dropdown-trigger {
      min-height: 0;
      margin-top: 8px;
      padding: 12px 12px 12px 16px;
    }
  }
  /* 值文字吃掉剩餘寬度，過長時省略；caret 固定 20px 不被壓縮。 */
  .dropdown-value {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .dropdown-caret {
    flex: none;
    width: 20px;
    height: 20px;
    color: var(--mp-text-secondary);
  }
  /* 未選擇時「請選擇」用 placeholder 灰（Figma text/placeholder）。 */
  .dropdown-trigger.is-placeholder .dropdown-value {
    color: var(--mp-text-placeholder);
  }
  /* 展開中與 focus 同樣是 border/focus（#006cfecc = --mp-brand-weak）。 */
  .dropdown-trigger:focus,
  .dropdown-trigger[aria-expanded='true'] {
    border-color: var(--mp-brand-weak);
    outline: none;
  }
  /* 驗證未通過（Figma State=Error 133:729）：邊線轉 alert 色，且要贏過上面那兩條。 */
  .dropdown-trigger.is-error,
  .dropdown-trigger.is-error:focus,
  .dropdown-trigger.is-error[aria-expanded='true'] {
    border-color: var(--mp-alert);
  }
  /* Error 態的焦點指示。上面那條把邊線顏色釘死成 alert，於是標紅的下拉題 focus 前後的
     computed style 一模一樣（同色、同 1px），而這個觸發框又自己關掉了 host 層的通用 focus
     ring（上面 :focus 那條的 outline: none）—— 鍵盤使用者按 Tab 走到它上面完全沒有回饋。
     用 inset shadow 在邊線內側補一圈，視覺上等於邊框加粗到 2px：不外擴（外擴會超出
     width:100% 的觸發框、可能被捲動容器裁掉），也不進 layout。
     設計稿沒有 Error×Focused 這一格（133:735 的六個狀態互斥），這是補的。
     同型的處理在 question.ts 的 .line-input.is-error:focus-visible。 */
  .dropdown-trigger.is-error:focus-visible {
    box-shadow: inset 0 0 0 1px var(--mp-alert);
  }
  /* forced-colors（Windows 高對比）丟掉 box-shadow、把 border-color 換成系統色，於是上面
     兩種指示（一般態的邊線轉 brand、error 態的 inset ring）都不呈現。這個觸發框在 :focus
     那條把 outline 關掉了，所以高對比下要把它還回來 —— 同 question.ts 的那一段。
     這裡用外擴的 outline-offset，跟上面選 inset 的理由不衝突：上面避免外擴是為了常態下不
     頂到捲動容器；在這個模式下沒有別的選擇（box-shadow 一律被丟掉），而 frame-body 的
     左右內距有 16px（≥640 為 32px）餘裕接得住這 2px。 */
  @media (forced-colors: active) {
    .dropdown-trigger:focus-visible {
      outline: 2px solid;
      outline-offset: 2px;
    }
  }

  /* 面板：方角（Figma radius/0）、border/sub 細框、無陰影。位置與 max-height 由 JS 逐次
     寫進 style（見 DropdownOverlay.position）—— 它在 top layer，座標以 viewport 為準。 */
  .dropdown-pop {
    position: fixed;
    /* UA 給 dialog 的是 inset:0。只覆寫 margin 與 JS 寫的 top/left 會讓 right/bottom 留在 0，
       面板不被拉伸就得仰賴各家 UA 自己帶的 height:fit-content（Chrome 有，不保證每家都有；
       此 repo 的 baseline 還含 iOS Safari 16 與 Firefox ESR 115）。明確設 inset:auto，尺寸才
       確定由內容決定；JS 寫的 top/left 是 inline style，仍然勝過這裡。 */
    inset: auto;
    display: flex;
    box-sizing: border-box;
    max-width: 100vw;
    flex-direction: column;
    margin: 0;
    padding: 0;
    border: 1px solid var(--mp-border-sub);
    border-radius: 0;
    background: var(--mp-bg-base);
    color: var(--mp-text-primary);
    overflow: hidden;
  }
  /* 選單不該把頁面壓暗 —— backdrop 只負責接住「點外面關閉」，不做視覺。 */
  .dropdown-pop::backdrop {
    background: transparent;
  }
  .dropdown-list {
    flex: 1 1 auto;
    min-height: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    overflow-y: auto;
    overscroll-behavior: contain;
    outline: none;
  }
  /* 選項列（Figma 133:706）：42 列高 = py8 + 26 行高；label x16、右側 24px 雙勾距右緣 16。 */
  .dropdown-opt {
    display: flex;
    box-sizing: border-box;
    min-height: 42px;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    color: var(--mp-text-secondary);
    font-size: 18px;
    line-height: 26px;
    letter-spacing: 0.5px;
    cursor: pointer;
  }
  .dropdown-opt-label {
    flex: 1 1 auto;
    min-width: 0;
    overflow-wrap: break-word;
  }
  .dropdown-check {
    flex: none;
    width: 24px;
    height: 24px;
  }
  /* 鍵盤 active 與滑鼠 hover 共用同一層淡灰疊色（設計稿沒有 active 列的規格，
     沿用 Controls 的 pressed 疊色語彙）。 */
  .dropdown-opt:hover,
  .dropdown-opt.is-active {
    background: var(--mp-btn-pressed);
  }
  /* 已選列（Figma 133:708 Option / Selected）：brand 淡底 + brand 字 + 雙勾。
     寫在 hover/active 之後才贏得過它們（同為 0,2,0，靠順序）。 */
  .dropdown-opt[aria-selected='true'] {
    background: var(--mp-brand-soft);
    color: var(--mp-brand);
  }
  /* 已選 + hover/active：疊色而非換色，否則游標一經過就看不出哪個是已選的。
     用 linear-gradient 疊而非 color-mix()，後者 Safari 16 沒有（見 browser baseline）。 */
  .dropdown-opt[aria-selected='true']:hover,
  .dropdown-opt[aria-selected='true'].is-active {
    background: linear-gradient(var(--mp-btn-pressed), var(--mp-btn-pressed)), var(--mp-brand-soft);
  }
`

// ---------------------------------------------------------------------------
// 模板
// ---------------------------------------------------------------------------

export interface DropdownState {
  optionAnswers: Record<string, string[]>
  /** 目前展開中的題目 id；undefined = 全部收合。 */
  openDropdownQId?: string | undefined
  /** 展開中面板的 active option index（鍵盤游標），-1 = 無。 */
  dropdownActiveIndex?: number | undefined
}

export interface DropdownHandlers {
  toggleOption: (qId: string, optId: string, multi: boolean) => void
  openDropdown: (qId: string, activeIndex: number) => void
  closeDropdown: () => void
  setDropdownActive: (index: number) => void
}

interface ListboxKeyContext {
  qKey: string
  labels: string[]
  optionIds: string[]
  activeIndex: number
  handlers: DropdownHandlers
}

// 面板內的鍵盤模型。Escape 不在這裡處理 —— <dialog> 原生 cancel 就會關閉，攔下來只會多一條路徑。
export function onListboxKeydown(e: KeyboardEvent, ctx: ListboxKeyContext): void {
  const { key } = e
  if (key === 'Escape')
    return
  // showModal 會把 Tab 困在面板內；關閉後焦點還給觸發框，Tab 才能繼續往下走。
  if (key === 'Tab') {
    e.preventDefault()
    ctx.handlers.closeDropdown()
    return
  }
  if (key === 'Enter' || key === ' ') {
    e.preventDefault()
    const id = ctx.optionIds[ctx.activeIndex]
    if (id !== undefined)
      ctx.handlers.toggleOption(ctx.qKey, id, false)
    ctx.handlers.closeDropdown()
    return
  }
  const moved = nextActiveIndex(key, ctx.activeIndex, ctx.labels.length)
  if (moved !== null) {
    e.preventDefault()
    ctx.handlers.setDropdownActive(moved)
    return
  }
  // 可列印字元 → type-ahead。修飾鍵組合（複製貼上等）不算。
  if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    const idx = typeAheadIndex(ctx.labels, pushTypeAhead(key), ctx.activeIndex)
    if (idx !== null) {
      e.preventDefault()
      ctx.handlers.setDropdownActive(idx)
    }
  }
}

// 收合狀態下的觸發框鍵盤：上下鍵開啟。Enter / Space 由 <button> 原生轉成 click，不必另外處理。
export function onTriggerKeydown(e: KeyboardEvent, qKey: string, pickedIndex: number, handlers: DropdownHandlers): void {
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    handlers.openDropdown(qKey, pickedIndex < 0 ? 0 : pickedIndex)
  }
}

export interface DropdownViewOpts {
  q: Question
  /** 題目 id 正規化後的 token（由 question.ts 的 qIdToken 產生），用來組面板內元素的 id。 */
  token: string
  /** 錯誤說明文字的 id，invalid 時掛在 aria-describedby 上。 */
  errorId: string
  state: DropdownState
  handlers: DropdownHandlers
  invalid: boolean
  /** 撐高層（見 question.ts 的 QuestionState.sizing）：不輸出 data-dd-trigger 與 aria-controls。 */
  sizing?: boolean
}

// DROPDOWN 在資料層是單選 —— 答案走 toggleOption 的單選路徑（multi=false）。
export function dropdownView(opts: DropdownViewOpts): TemplateResult {
  const { q, token, errorId, state, handlers, invalid } = opts
  const sizing = opts.sizing === true
  const qKey = String(q.id)
  const options = q.options ?? []
  const labels = options.map(o => o.content)
  const optionIds = options.map(o => String(o.seq))
  const picked = (state.optionAnswers[qKey] ?? [])[0] ?? ''
  const pickedIndex = optionIds.indexOf(picked)
  const open = state.openDropdownQId === qKey
  const activeIndex = open ? (state.dropdownActiveIndex ?? -1) : -1
  const listId = `dd-list-${token}`
  const optDomId = (i: number): string => `dd-opt-${token}-${i}`

  return html`
    <button
      type="button"
      class="dropdown-trigger${pickedIndex < 0 ? ' is-placeholder' : ''}${invalid ? ' is-error' : ''}"
      data-dd-trigger=${sizing ? nothing : qKey}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded=${open ? 'true' : 'false'}
      aria-controls=${sizing ? nothing : listId}
      aria-label=${q.text}
      aria-invalid=${invalid ? 'true' : nothing}
      aria-describedby=${invalid ? errorId : nothing}
      @click=${() => open ? handlers.closeDropdown() : handlers.openDropdown(qKey, pickedIndex < 0 ? 0 : pickedIndex)}
      @keydown=${(e: KeyboardEvent) => onTriggerKeydown(e, qKey, pickedIndex, handlers)}
    >
      <span class="dropdown-value">${pickedIndex < 0 ? '請選擇' : labels[pickedIndex]}</span>
      ${open ? iconCaretUp() : iconCaretDown()}
    </button>
    ${open
      ? html`
          <dialog
            class="dropdown-pop"
            data-dd-pop=${qKey}
            @click=${(e: MouseEvent) => {
              // dialog 的框只涵蓋面板本身；點 backdrop 時 target 就是 dialog 自己。
              if (e.target === e.currentTarget)
                handlers.closeDropdown()
            }}
          >
            <ul
              class="dropdown-list"
              id=${listId}
              role="listbox"
              tabindex="0"
              aria-label=${q.text}
              aria-activedescendant=${activeIndex >= 0 ? optDomId(activeIndex) : nothing}
              @keydown=${(e: KeyboardEvent) => onListboxKeydown(e, { qKey, labels, optionIds, activeIndex, handlers })}
            >
              ${options.map((o, i) => html`
                <li
                  class="dropdown-opt${i === activeIndex ? ' is-active' : ''}"
                  id=${optDomId(i)}
                  role="option"
                  aria-selected=${optionIds[i] === picked ? 'true' : 'false'}
                  @click=${() => {
                    handlers.toggleOption(qKey, optionIds[i] ?? '', false)
                    handlers.closeDropdown()
                  }}
                >
                  <span class="dropdown-opt-label">${o.content}</span>
                  ${optionIds[i] === picked ? iconChecks() : nothing}
                </li>
              `)}
            </ul>
          </dialog>
        `
      : nothing}
  `
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

interface DropdownHost extends ReactiveControllerHost {
  renderRoot: DocumentFragment | HTMLElement
}

// 沿用 sidebar-shell 的 SidebarOverlay 形狀：開關狀態、showModal 呼叫、焦點還原都在 controller，
// 不散進 survey-element 的作答 state。差別是 dropdown 不鎖背景捲動 —— showModal 已讓背景 inert，
// 而選單本來就該跟著版面走，所以改成捲動時重新定位。
export class DropdownOverlay implements ReactiveController {
  /** 展開中的題目 id；undefined = 收合。 */
  openQId: string | undefined = undefined
  /** 面板內的鍵盤游標。 */
  activeIndex = -1

  private positioned = false
  private rafId = 0
  // 關閉後要把焦點還回去的觸發框。這裡刻意記「觸發框」而不是 sidebar overlay 那樣記「開啟前的
  // activeElement」—— 面板一定是從某個觸發框展開的，還原目標明確；記 activeElement 的話，
  // 用非焦點途徑開啟（程式化、指標點擊未先聚焦）時會抓到 null，焦點就掉回 <body>。
  private triggerEl: HTMLElement | null = null

  // metrics 預設是真 DOM 量測；測試餵座標可控的替身（jsdom 的 rect 與 offsetHeight 都是 0）。
  constructor(private host: DropdownHost, private metrics: PanelMeasure = domPanelMeasure()) {
    host.addController(this)
  }

  private dialog(): HTMLDialogElement | null {
    return this.host.renderRoot.querySelector('.dropdown-pop')
  }

  private list(): HTMLElement | null {
    return this.host.renderRoot.querySelector('.dropdown-list')
  }

  // 用 dataset 逐一比對而不是把 qId 串進選擇器：題目 id 來自後端，含引號或選擇器 meta 字元時
  // 字串拼接會壞掉，而 CSS.escape 又不是每個執行環境都有（jsdom 就沒有）。
  private trigger(): HTMLElement | null {
    if (this.openQId === undefined)
      return null
    const all = this.host.renderRoot.querySelectorAll<HTMLElement>('[data-dd-trigger]')
    for (const el of all) {
      if (el.dataset.ddTrigger === this.openQId)
        return el
    }
    return null
  }

  open = (qId: string, activeIndex: number): void => {
    if (this.openQId !== qId)
      this.resetPlacement()
    resetTypeAhead()
    this.openQId = qId
    this.activeIndex = activeIndex
    this.host.requestUpdate()
  }

  setActive = (index: number): void => {
    this.activeIndex = index
    this.host.requestUpdate()
  }

  // 程式化關閉：關掉 dialog 後**自己**收斂狀態，不等原生 close 事件。
  // close 事件是非同步派送的（規格：queue 一個 element task），只靠它收斂會讓這一幀的
  // 渲染還停在展開態；close 事件那條路留給 Esc 等原生關閉，onClose 有防重入。
  close = (): void => {
    const dlg = this.dialog()
    if (dlg?.open && typeof dlg.close === 'function')
      dlg.close()
    this.onClose()
  }

  private onClose = (): void => {
    if (this.openQId === undefined)
      return
    this.openQId = undefined
    this.activeIndex = -1
    this.resetPlacement()
    this.detachViewportListeners()
    // Lit 重渲會沿用同一個 <button> 節點，所以開啟時抓到的參照關閉後仍有效。
    const trigger = this.triggerEl
    this.triggerEl = null
    trigger?.focus?.()
    this.host.requestUpdate()
  }

  // 每個畫面格最多重新定位一次。scroll 在宿主頁上是高頻事件，逐次同步重排會讓捲動卡頓。
  private onViewportChange = (): void => {
    if (this.rafId !== 0)
      return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = 0
      this.position()
    })
  }

  // 要掛兩個地方才收得齊，因為 scroll 既不冒泡、composed 也是 false（已實測）：
  //   window 的 capture —— 收宿主頁那一側的捲動容器，以及 window 自身。
  //   renderRoot 的 capture —— 收 widget 自己 shadow root 內的 .frame-body。那個 scroll
  //     到不了 window（composed false 擋在 shadow 邊界），只掛 window 的話，使用者捲作答區時
  //     面板會留在原地與觸發框脫節。
  // 兩邊不會重複觸發：同一個 scroll 只存在於其中一棵樹裡。
  private attachViewportListeners(): void {
    window.addEventListener('scroll', this.onViewportChange, true)
    window.addEventListener('resize', this.onViewportChange)
    this.host.renderRoot.addEventListener('scroll', this.onViewportChange, true)
  }

  private detachViewportListeners(): void {
    window.removeEventListener('scroll', this.onViewportChange, true)
    window.removeEventListener('resize', this.onViewportChange)
    this.host.renderRoot.removeEventListener('scroll', this.onViewportChange, true)
    if (this.rafId !== 0) {
      cancelAnimationFrame(this.rafId)
      this.rafId = 0
    }
  }

  // 下次開啟要重新定位、也要重新量自然高度。兩者一起重置 —— 分開的話，量測快取活得比一次
  // 開啟還久，換到另一題時會沿用上一個面板的高度。
  private resetPlacement(): void {
    this.positioned = false
    this.metrics.invalidate()
  }

  private position(): void {
    const dlg = this.dialog()
    const trg = this.trigger()
    if (!dlg || !trg)
      return
    const m = this.metrics.measure(dlg, trg)
    const p = panelPlacement(m)
    dlg.style.left = `${m.triggerLeft}px`
    dlg.style.width = `${m.triggerWidth}px`
    dlg.style.top = `${p.top}px`
    dlg.style.maxHeight = `${p.maxHeight}px`
  }

  private scrollActiveIntoView(): void {
    if (this.activeIndex < 0)
      return
    const active = this.list()?.children[this.activeIndex] as HTMLElement | undefined
    active?.scrollIntoView?.({ block: 'nearest' })
  }

  // showModal 必須在 render 後（dialog 已在 DOM）且 imperative 呼叫 —— top layer 才逃得出宿主容器。
  hostUpdated(): void {
    const dlg = this.dialog()
    if (this.openQId === undefined || !dlg)
      return
    // showModal 存在性防護：baseline 瀏覽器皆支援 <dialog>，這是給 jsdom（測試環境未實作）留的。
    if (!dlg.open && typeof dlg.showModal === 'function') {
      dlg.addEventListener('close', this.onClose, { once: true })
      dlg.showModal()
    }
    if (!this.positioned) {
      this.positioned = true
      this.triggerEl = this.trigger()
      this.position()
      this.attachViewportListeners()
      this.list()?.focus?.()
    }
    this.scrollActiveIntoView()
  }

  // 宿主節點被移除（第三方 SPA 導航 / admin 預覽卸載）時 dialog 不發 close 事件，
  // 這裡兜底拆掉 window 上的 listener，否則會留下指向已卸載元件的參照。
  hostDisconnected(): void {
    this.detachViewportListeners()
    this.openQId = undefined
    this.activeIndex = -1
    this.resetPlacement()
    this.triggerEl = null
  }
}
